/* ========================================================================
   © 2026 FixMySiteKit Framework. All rights reserved.
   Directory Niche — /solutions/:niche
   Lists all 50 metro branches for a specific trade vertical.
   ======================================================================== */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Timer, Zap } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import DirectoryLoading from "@/components/DirectoryLoading";
import useSeoHead from "@/lib/useSeoHead";
import { API_BASE } from "@/lib/api";

const CANONICAL_HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function DirectoryNiche() {
    const { niche } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, niche: null, branches: [] });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/directory/niche/${niche}`, { credentials: "omit" });
                if (res.status === 404) {
                    if (!cancelled) navigate("/404-directory", { replace: true });
                    return;
                }
                const data = await res.json();
                if (!cancelled) {
                    setState({ loading: false, niche: data.niche, branches: data.branches || [] });
                }
            } catch (_) {
                if (!cancelled) setState({ loading: false, niche: null, branches: [] });
            }
        })();
        return () => { cancelled = true; };
    }, [niche, navigate]);

    const canonical = `${CANONICAL_HOST}/solutions/${niche}`;
    const n = state.niche;

    useSeoHead({
        title: n
            ? `${n.label} Near Me — Verified Branches in 50 US Metros | FixMySiteKit`
            : "Verified Regional Branches | FixMySiteKit",
        description: n
            ? `${n.label} dispatch across the top 50 US metros. Average operator response inside ${n.sla} minutes. Speed-to-lead relay wired to every branch.`
            : "Verified regional branches across the top 50 US metros.",
        canonical,
        keywords: n ? [n.label, ...(n.keywords || []), "near me", "24/7 dispatch"].join(", ") : "",
        jsonLd: n
            ? {
                  "@context": "https://schema.org",
                  "@type": "Service",
                  serviceType: n.label,
                  provider: {
                      "@type": "Organization",
                      name: "FixMySiteKit",
                      url: CANONICAL_HOST || "https://fixmysitekit.com",
                  },
                  areaServed: state.branches.map((b) => ({
                      "@type": "City",
                      name: `${b.metroCity}, ${b.metroState}`,
                  })),
                  description: `${n.label} dispatch across ${state.branches.length} verified US metro branches.`,
              }
            : null,
    });

    if (state.loading) return <DirectoryLoading label="Loading branches…" />;
    if (!n) {
        return (
            <div className="mx-auto max-w-2xl px-6 py-24 text-center">
                <h1 className="font-display text-2xl font-bold">Vertical not found</h1>
                <Link to="/solutions" className="mt-4 inline-block text-primary underline">
                    Back to directory
                </Link>
            </div>
        );
    }

    return (
        <div
            data-testid="directory-niche"
            className="min-h-screen bg-background text-foreground"
        >
            <header
                className="border-b border-border bg-background/85 backdrop-blur"
                role="banner"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
                    <Link to="/" className="inline-flex items-center gap-2" aria-label="FixMySiteKit home">
                        <span className="grid h-8 w-8 place-items-center bg-primary text-primary-foreground">
                            <Zap className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className="font-display text-lg font-bold tracking-tight">FixMySiteKit</span>
                    </Link>
                    <Link
                        to="/solutions"
                        data-testid="directory-back-link"
                        className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground"
                    >
                        ← All Verticals
                    </Link>
                </div>
            </header>

            <main role="main">
                <section aria-labelledby="niche-heading" className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
                        <nav aria-label="Breadcrumb" className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                            <Link to="/solutions" className="hover:text-foreground">
                                Directory
                            </Link>{" "}
                            / <span className="text-primary">{n.label}</span>
                        </nav>
                        <h1
                            id="niche-heading"
                            data-testid="directory-niche-heading"
                            className="mt-3 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
                        >
                            {n.label} dispatch, wired to the phone.
                        </h1>
                        <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
                            {state.branches.length} verified metro branches handling {n.problem}.
                            Average response inside <span className="text-foreground">{n.sla} minutes</span>.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="border border-border px-3 py-1.5 font-mono uppercase tracking-[0.2em]">
                                <Timer className="mr-2 inline h-3.5 w-3.5" aria-hidden="true" />
                                {n.sla} min SLA
                            </span>
                            <span className="border border-border px-3 py-1.5 font-mono uppercase tracking-[0.2em]">
                                Avg ticket {n.avgTicket}
                            </span>
                            <span className="border border-border px-3 py-1.5 font-mono uppercase tracking-[0.2em]">
                                {n.radius} coverage
                            </span>
                        </div>
                    </div>
                </section>

                <section aria-labelledby="branch-list-heading" className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <h2
                            id="branch-list-heading"
                            className="font-display text-2xl font-bold tracking-tight md:text-3xl"
                        >
                            Pick a metro
                        </h2>
                        <ul
                            className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                            data-testid="directory-branch-grid"
                        >
                            {state.branches.map((b) => (
                                <li key={b.locationSlug}>
                                    <Link
                                        to={`/solutions/${niche}/${b.locationSlug}`}
                                        data-testid={`branch-card-${b.locationSlug}`}
                                        className="group block h-full border border-border bg-card p-5 transition-colors hover:border-primary/60 hover:bg-primary/5"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                                    <MapPin className="mr-1.5 inline h-3 w-3" aria-hidden="true" />
                                                    {b.metroState}
                                                </p>
                                                <h3 className="mt-2 font-display text-base font-bold">
                                                    {b.locationLabel}
                                                </h3>
                                            </div>
                                            <ArrowRight
                                                className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <p className="mt-3 text-xs text-muted-foreground">
                                            {b.operatorCount} operators · {b.responseTimeSlaMinutes}-min avg
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <div className="border border-border bg-card p-8 md:p-10">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                Own the phone that rings
                            </p>
                            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight md:text-3xl">
                                Route your {n.label.toLowerCase()} leads through the same relay.
                            </h2>
                            <Link
                                to="/"
                                data-testid="niche-cta-funnel"
                                className="mt-6 inline-flex items-center gap-2 bg-primary px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform hover:-translate-y-0.5"
                            >
                                Activate my branch
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <SiteFooter />
        </div>
    );
}
